// #/services/registry/zodFromJson/index.ts

import { ZodSchema, z, ZodTypeAny } from 'zod';

import { ActionDetails } from '#/types/registry';

export function createZodSchemaFromJsonSchema(schema: any): ZodSchema | null {
  try {
    // console.debug('createZodSchemaFromJsonSchema(schema:', schema, ');');

    if (schema == null) {
      return null;
    }

    // Array
    if (Array.isArray(schema)) {
      // Handle array schema
      // const subschemas = schema.map((s: any) => {
      //   if (s == null) {
      //     throw new Error('Null schema in array');
      //   }
      //   return createZodSchemaFromJsonSchema(s);
      // });
      const subschemas: ZodTypeAny[] = [];
      for (const s of schema) {
        if (s == null) {
          throw new Error('Null schema in array');
        }
        const subschema = createZodSchemaFromJsonSchema(s);
        if (subschema == null) {
          throw new Error('Null schema in array');
        }
        subschemas.push(subschema);
      }
      if (subschemas.length === 0) {
        throw new Error('Empty array schema');
      }
      return z.tuple(subschemas as [] | [ZodTypeAny, ...ZodTypeAny[]]);
    }

    // Object
    if (typeof schema === 'object') {
      if (schema?.oneOf != null) {
        return z.union(
          schema?.oneOf.map((s: any) => createZodSchemaFromJsonSchema(s)),
        );
      }

      if (schema?.type === 'array' && schema?.itemSchema != null) {
        const zodItemSchema = createZodSchemaFromJsonSchema(schema.itemSchema);
        if (zodItemSchema == null) {
          throw new Error('Null item schema for array');
        }
        return z.array(zodItemSchema);
      }

      // Handle object schema

      const properties = schema?.properties;
      const required = schema?.required ?? [];

      const parsedProperties: { [key: string]: ZodSchema<any> } = {};

      if (properties == null) {
        return z.object({});
      }

      for (const [propertyName, propertySchema] of Object.entries(properties)) {
        if (propertySchema == null) {
          continue;
        }
        const zodPropertySchema = createZodSchemaFromJsonSchema(propertySchema);
        if (propertyName != null && zodPropertySchema != null) {
          if (required.includes(propertyName)) {
            parsedProperties[propertyName] = zodPropertySchema;
          } else {
            // TODO: optional fields (requires support from dynamic form)
            // zodPropertySchema.optional();
          }
        }
      }

      return z.object(parsedProperties);
    }

    // Primitive
    switch (schema) {
      case 'null': {
        return z.null();
      }
      case 'string': {
        return z.string();
      }
      case 'number': {
        return z.number();
      }
      case 'boolean': {
        return z.boolean();
      }
      case 'integer':
      case 'bigint': {
        return z.number().int();
      }
      case 'object': {
        return z.record(z.any());
      }
      // Add additional primitive types as needed
      default:
        throw new Error(`Unsupported schema type: ${schema}`);
    }
  } catch (error: any) {
    console.error(error);
    throw new Error(`zod from json error: ${error.message}`);
  }
}

export function zodSchemasFromActionDetails(actionDetails: ActionDetails) {
  try {
    console.info(
      `zodSchemasFromActionDetails(actionDetails: ${JSON.stringify(
        actionDetails,
      )}`,
    );

    let inputPathParamsZodSchema;
    let inputQueryParamsZodSchema;
    let inputBodyJsonParamsZodSchema;
    let outputSuccessResponseBodyJsonZodSchema;

    try {
      inputPathParamsZodSchema = createZodSchemaFromJsonSchema(
        actionDetails?.inputSchema?.path,
      );
    } catch (error) {
      console.debug({ label: 'inputPathParamsZodSchema', error });
      // console.error(error);
    }

    try {
      inputQueryParamsZodSchema = createZodSchemaFromJsonSchema(
        actionDetails.inputSchema.query,
      );
    } catch (error) {
      console.debug({ label: 'inputQueryParamsZodSchema', error });
      // console.error(error);
    }

    try {
      inputBodyJsonParamsZodSchema = createZodSchemaFromJsonSchema(
        actionDetails.inputSchema.bodyJson,
      );
    } catch (error) {
      console.debug({ label: 'inputBodyJsonParamsZodSchema', error });
      // console.error(error);
    }

    try {
      outputSuccessResponseBodyJsonZodSchema = createZodSchemaFromJsonSchema(
        actionDetails.outputSchema,
      );
    } catch (error) {
      console.debug({
        label: 'outputSuccessResponseBodyJsonZodSchema',
        error,
      });
      // console.error(error);
    }

    // console.debug({
    //   inputPathParamsZodSchema,
    //   inputQueryParamsZodSchema,
    //   inputBodyJsonParamsZodSchema,
    //   outputSuccessResponseBodyJsonZodSchema,
    // });

    return {
      inputSchema: {
        path: inputPathParamsZodSchema,
        query: inputQueryParamsZodSchema,
        bodyJson: inputBodyJsonParamsZodSchema,
      },
      outputSchema: outputSuccessResponseBodyJsonZodSchema,
    };
  } catch (err) {
    console.error(
      `Error parsing zod schemas from action details ${actionDetails.serviceKey} ${actionDetails.actionKey}: ${err}`,
    );
  }
}
